import { defineStore } from 'pinia'
import { Activity } from "@/models/Activity.model";
import { orgApi } from "@/utils/Api.util";
import { Icon, FileIcon, SpriteIcon } from "@/models/Icon.model";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { ErrorMessageBag } from "@/stores/errors/ErrorMessageBag.ts";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import { ActivityFilterDef } from "@/stores/filters/ActivityFilterDef";

const activityMask: string[] = [
	'id',
	'name',
	'seats',
	'color',
	'symbol',
	'icon.*',
	'icon.library.id'
];

interface ActivityStoreState {
	errors: ErrorMessageBag | null,
	loading: boolean,
	activities: Activity[],
	activitiesMap: { [id: string]: Activity },
	loadedClassroomId: string | null,
	activityNamesForAllMyClassrooms: string[],
	usedIconsMap: { [key: string | number]: Activity },
	_filter: ActivityFilterDef,
	_defaultFilter: ActivityFilterDef,
}

export const useActivitiesStore = defineStore('activities', {

	state: (): ActivityStoreState => ({
		errors: null,
		loading: false,
		activities: [],
		activitiesMap: {},
		loadedClassroomId: null,
		activityNamesForAllMyClassrooms: [],
		usedIconsMap: {},
		_filter: new ActivityFilterDef(),
		_defaultFilter: new ActivityFilterDef(),
	}),

	getters: {
		isIconUsed: (state) => {
			return (icon: Icon) => {
				if(icon instanceof SpriteIcon && icon.url && typeof (state.usedIconsMap[icon.url]) !== 'undefined') {
					return true;
				} else if (icon instanceof FileIcon && icon.id && typeof (state.usedIconsMap[icon.id]) !== 'undefined') {
					return true;
				} else {
					return false;
				}
			}
		},
	},

	actions: {

		new() {
			let newActivity = new Activity();
			newActivity.classroomId = useClassroomsStore().getCurrentClassroom().id.toString();
			newActivity.seats = 2;	// default seats number

			return newActivity;
		},

		async load() {

			const classroomId = useClassroomsStore().getCurrentClassroom().id;

			// Only load when not already loaded
			if (this.activities.length > 0 && this.loadedClassroomId === classroomId) {
				return;
			}

			let params: any = {};

			params.classroomIds = useClassroomsStore().getCurrentClassroom().id;
			params.mask = activityMask.join(',');
			Object.assign(params, this._filter.toApiParams());

			this.loadedClassroomId = classroomId;
			this.loading = true;

			try {
				const response = await orgApi.get('activities', {
					params: params
				});

				this.activities = response.data.data.map(Activity.mapFromServer);
				this.updateMap();

			this.sortItemsBy('name');

				this.updateUsedIconMaps();
			} finally {
				this.loading = false;
			}
		},

		async getActivityNames(currentClassroomOnly: boolean = true) {

			if(currentClassroomOnly) {
				await this.load();
				return this.activities.map(item => item.name);
			} else {

				if(this.activityNamesForAllMyClassrooms.length > 0) {
					return this.activityNamesForAllMyClassrooms;
				}

				const classroomIds = useClassroomsStore().classrooms.map(cr => cr.id);

				const response = await orgApi.get('activities', {
					params: {
						classroomIds: classroomIds.join(','),
						mask: 'name'
					}
				});

				this.activityNamesForAllMyClassrooms = response.data.data.map(item => item.name);
				return this.activityNamesForAllMyClassrooms;
			}
		},

		findById(id: string): Activity | null {
			if (this.activitiesMap[id]) {
				return this.activitiesMap[id];
			}
			return null;
		},

		findByIds(ids: string[]) {
			return this.activities.filter(p => ids.indexOf(p.id) !== -1)
		},

		async save(activity: Activity) {

			this.clearErrors();

			if(activity.id) {
				// update existing pupil
				try {
					const response = await orgApi.put('activities/' + activity.id, activity.getServerData());
					this.updateStoreItem(activity);
				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}

			} else {
				// create new pupil
				try {
					const response = await orgApi.post('activities', activity.getServerData());
					activity.id = response.data.data.id;
					this.addStoreItem(activity);
				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}
			}

			this.sortItemsBy('name');
		},

		async delete(activity: Activity) {

			this.clearErrors();

			try {
				const response = await orgApi.delete('activities/' + activity.id);
				this.removeStoreItem(activity);
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		updateStoreItem(activity: Activity) {
			const storeIndex = this.activities.findIndex(
				storeItem => storeItem.id == activity.id
			);
			this.activities[storeIndex] = activity.clone();
		},

		addStoreItem(activity: Activity) {
			this.activities.push(activity);
		},

		removeStoreItem(activity: Activity) {
			const index = this.activities.findIndex(v => v.id === activity.id);
			if (index >= 0) {
				this.activities.splice(index, 1);
			}
		},

		clearErrors() {
			this.errors = null;
		},

		sortItemsBy(attr) {
			// Sort on name (for now)
			this.activities.sort(
				(a: Activity, b: Activity) => {
				return a[attr].localeCompare(b[attr]);
				}
			);
		},

		// getFromId(id: string): Activity | null {
		// 	return this.pupils.find(
		// 		storeItem => storeItem.id == id
		// 	);
		// },

		updateUsedIconMaps() {

			if(!this.loadedClassroomId) {
				return;
			}

			this.usedIconsMap = {};

			this.activities.forEach(
				(activity: Activity) => {
					if(activity.icon instanceof SpriteIcon) {
						this.usedIconsMap[activity.icon.url] = activity;
					} else if(activity.icon instanceof FileIcon) {
						this.usedIconsMap[activity.icon.id] = activity;
					}
				}
			);

		},

		updateMap() {
			this.activities.forEach(activity => {
				this.activitiesMap[activity.id] = activity;
			});
		},

		getCurrentFilter(): ActivityFilterDef {
			return this._filter.clone() as ActivityFilterDef;
		},

		getDefaultFilter(): ActivityFilterDef {
			return this._defaultFilter.clone() as ActivityFilterDef;
		},

		async applyFilter(filter: ActivityFilterDef) {
			this._filter = filter;
			this.loadedClassroomId = null;
			await this.load();
		},

		async resetFilter() {
			const filter = this._defaultFilter.clone() as ActivityFilterDef;
			await this.applyFilter(filter);
		},

	}

});
